import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { apiErrorMessage } from "./pitchTempoUtils";
import { deletePublicSession, mediaUrl, publicToolHeaders } from "../lib/publicAudioTools";
import { PITCH_API, pollJob, uploadChunks } from "../lib/pitchStreamingApi";

export function spaceBelongsToPlayer(event, element) {
  if (event.code !== "Space" || event.repeat || event.defaultPrevented || event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return false;
  if (document.visibilityState !== "visible" || !document.hasFocus() || !element?.contains(event.target)) return false;
  return !event.target.closest("input,textarea,select,button,a,summary,audio,video,[contenteditable]:not([contenteditable='false']),[role=button],[role=slider],[role=checkbox],[role=menuitem],[role=textbox],[role=combobox],[role=switch],[role=radio],[role=tab],[role=listbox],[role=option]");
}

export default function usePitchTempoPlayer(accept) {
  const rootRef = useRef(null), audioRef = useRef(null), sessionRef = useRef(null), engineRef = useRef(null);
  const operationRef = useRef(null), exportRef = useRef(null), fileRef = useRef(null), generationRef = useRef(0);
  const playIntentRef = useRef(false), playVersionRef = useRef(0);
  const playPendingRef = useRef(false);
  const lastActivityRef = useRef(0);
  const settingsRef = useRef({ mode: "processed", pitch: 0, ratio: 1 });
  const [phase, setPhase] = useState("EMPTY"), [error, setError] = useState("");
  const [session, setSession] = useState(null), [playing, setPlaying] = useState(false), [playhead, setPlayhead] = useState(0);
  const [mode, setMode] = useState("processed"), [pitch, setPitch] = useState(0), [ratio, setRatio] = useState(1);
  const [progress, setProgress] = useState(0), [stage, setStage] = useState("");
  const [exporting, setExporting] = useState(false), [exportProgress, setExportProgress] = useState(0);
  const localUrlRef = useRef(null), [originalUrl, setOriginalUrl] = useState("");

  const touchActivity = useCallback(() => {
    const current = sessionRef.current;
    if (!current || Date.now() - lastActivityRef.current < 30000) return;
    lastActivityRef.current = Date.now();
    axios.get(`${PITCH_API}/${current.session_id}/status`, { headers: publicToolHeaders(current) }).catch((failure) => {
      if (failure.response?.status === 410 && sessionRef.current?.session_id === current.session_id) {
        setError("La sesion del servidor caduco por inactividad. Vuelve a subir el archivo para exportarlo.");
      }
    });
  }, []);

  const change = (next) => {
    touchActivity();
    settingsRef.current = { ...settingsRef.current, ...next };
    engineRef.current?.apply(settingsRef.current);
    if (next.pitch !== undefined) setPitch(next.pitch);
    if (next.ratio !== undefined) setRatio(next.ratio);
    if (next.mode !== undefined) setMode(next.mode);
  };
  const changePitch = (value) => { if (Number.isFinite(value)) change({ pitch: Math.max(-12, Math.min(12, Math.round(value))) }); };
  const changeRatio = (value) => { if (Number.isFinite(value)) change({ ratio: Math.max(.5, Math.min(2, Math.round(value * 1000) / 1000)) }); };

  const dispose = useCallback(() => {
    playIntentRef.current = false;
    playPendingRef.current = false;
    ++playVersionRef.current;
    operationRef.current?.abort();
    exportRef.current?.abort();
    engineRef.current?.dispose();
    engineRef.current = null;
    if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
    localUrlRef.current = null;
  }, []);

  useEffect(() => () => {
    ++generationRef.current;
    dispose();
    // Unconfirmed navigation/disconnection abandons the server session to inactivity cleanup.
  }, [dispose]);

  const remove = async () => {
    if (!window.confirm("¿Eliminar este audio y sus archivos temporales?")) return false;
    playIntentRef.current = false;
    playPendingRef.current = false;
    ++playVersionRef.current;
    engineRef.current?.pause();
    audioRef.current?.pause();
    if (sessionRef.current) {
      try {
        await axios.delete(`${PITCH_API}/${sessionRef.current.session_id}`, { headers: publicToolHeaders(sessionRef.current) });
      } catch (failure) {
        setError(apiErrorMessage(failure, "No se pudo eliminar la sesion. Espera a que termine la descarga o exportacion."));
        return false;
      }
    }
    ++generationRef.current;
    dispose();
    sessionRef.current = null;
    fileRef.current = null;
    setSession(null); setOriginalUrl(""); setPlaying(false); setPlayhead(0); setPhase("EMPTY"); setError("");
    change({ mode: "processed", pitch: 0, ratio: 1 });
    return true;
  };

  const upload = async (file, resume = false) => {
    if (!file || !file.size) { setError("El archivo esta vacio."); return; }
    if (!accept?.split(",").includes(`.${file.name.split(".").pop().toLowerCase()}`)) { setError("Formato no compatible o catalogo de formatos aun no disponible."); return; }
    if (!resume && sessionRef.current && !(await remove())) return;
    const generation = ++generationRef.current;
    operationRef.current?.abort();
    const controller = new AbortController();
    operationRef.current = controller;
    fileRef.current = file;
    setError(""); setProgress(0); setPhase("UPLOADING");
    try {
      if (!resume || !sessionRef.current) {
        const response = await axios.post(`${PITCH_API}/uploads`, { filename: file.name, file_size: file.size }, { signal: controller.signal });
        if (generation !== generationRef.current) { deletePublicSession("pitch-tempo", response.data); return; }
        sessionRef.current = response.data;
      }
      const pending = sessionRef.current;
      await uploadChunks(file, pending, controller.signal, setProgress);
      setPhase("ANALYZING"); setStage("waveform");
      const current = await axios.get(`${PITCH_API}/${pending.session_id}/status`, { headers: publicToolHeaders(pending), signal: controller.signal });
      if (!["running", "ready"].includes(current.data?.analysis?.state)) {
        await axios.post(`${PITCH_API}/${pending.session_id}/analyze`, {}, { headers: publicToolHeaders(pending), signal: controller.signal });
      }
      const ready = await pollJob(pending, "analysis", controller.signal, (job) => { setStage(job.stage); setProgress(job.percent); });
      if (generation !== generationRef.current) return;
      sessionRef.current = { ...pending, ...ready };
      // File-backed object URLs are streamed by the browser, not decoded into an AudioBuffer.
      localUrlRef.current = URL.createObjectURL(file);
      setOriginalUrl(localUrlRef.current);
      setSession(sessionRef.current); setPhase("READY");
      change({ mode: "processed", pitch: 0, ratio: 1 });
    } catch (failure) {
      if (controller.signal.aborted || generation !== generationRef.current) return;
      setError(failure.message && !failure.response ? failure.message : apiErrorMessage(failure, "No se pudo subir o analizar el audio. Puedes reintentar sin perder los fragmentos."));
      setPhase("ERROR");
    }
  };

  const ensureEngine = async () => {
    if (engineRef.current) return engineRef.current;
    const generation = generationRef.current;
    const { RealtimePitchEngine } = await import("../lib/realtimePitchEngine");
    if (generation !== generationRef.current || !audioRef.current) return null;
    if (!engineRef.current) engineRef.current = new RealtimePitchEngine(audioRef.current);
    engineRef.current.apply(settingsRef.current);
    return engineRef.current;
  };
  const togglePlayback = async () => {
    if (!session) return;
    touchActivity();
    const version = ++playVersionRef.current;
    if (playIntentRef.current || !audioRef.current.paused) {
      playPendingRef.current = false;
      playIntentRef.current = false; engineRef.current?.pause(); setPlaying(false); return;
    }
    playIntentRef.current = true;
    playPendingRef.current = true;
    setPlaying(true);
    try {
      const engine = await ensureEngine();
      if (audioRef.current?.ended) engine?.seek(0);
      await engine?.play(() => playIntentRef.current && version === playVersionRef.current);
    } catch (failure) {
      if (version !== playVersionRef.current) return;
      playIntentRef.current = false; setPlaying(false);
      setError(failure.message || "No se pudo iniciar el procesamiento de audio en este navegador.");
    } finally {
      if (version === playVersionRef.current) playPendingRef.current = false;
    }
  };
  const seek = (seconds) => {
    if (!session || !audioRef.current) return;
    touchActivity();
    const target = Math.max(0, Math.min(session.duration_seconds, seconds));
    if (engineRef.current) engineRef.current.seek(target);
    else audioRef.current.currentTime = target;
    setPlayhead(target);
  };
  const stop = () => { playIntentRef.current = false; playPendingRef.current = false; ++playVersionRef.current; engineRef.current?.pause(); audioRef.current?.pause(); seek(0); setPlaying(false); };

  useEffect(() => {
    const keydown = (event) => {
      if (document.visibilityState === "visible" && document.hasFocus()) touchActivity();
      if (spaceBelongsToPlayer(event, rootRef.current)) { event.preventDefault(); togglePlayback(); }
    };
    const root = rootRef.current;
    root?.addEventListener("keydown", keydown);
    root?.addEventListener("pointerdown", touchActivity);
    return () => { root?.removeEventListener("keydown", keydown); root?.removeEventListener("pointerdown", touchActivity); };
    // Keep the listener local to this page; native focused controls own Space.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, touchActivity]);

  useEffect(() => {
    if (!session) return undefined;
    let frame, last = 0;
    const update = (time) => {
      if (time - last > 50) { setPlayhead(audioRef.current?.currentTime || 0); last = time; }
      frame = requestAnimationFrame(update);
    };
    frame = requestAnimationFrame(update);
    const heartbeat = window.setInterval(() => {
      if (audioRef.current && !audioRef.current.paused) touchActivity();
    }, 30000);
    const onError = (event) => setError(event.detail);
    const audio = audioRef.current;
    audio?.addEventListener("dsp-error", onError);
    return () => { cancelAnimationFrame(frame); window.clearInterval(heartbeat); audio?.removeEventListener("dsp-error", onError); };
  }, [session, touchActivity]);

  const loadFallback = () => { if (session?.original_url && originalUrl !== mediaUrl(session.original_url)) setOriginalUrl(mediaUrl(session.original_url)); else setError("El navegador no pudo reproducir este audio."); };

  const exportAudio = async (format, quality = {}) => {
    if (!session || exportRef.current) return;
    const generation = generationRef.current;
    const controller = new AbortController(); exportRef.current = controller;
    setExporting(true); setExportProgress(0); setError("");
    try {
      await axios.post(`${PITCH_API}/${session.session_id}/export-jobs`, { pitch_semitones: pitch, tempo_ratio: ratio, format, quality }, { headers: publicToolHeaders(session), signal: controller.signal });
      const ready = await pollJob(session, "export", controller.signal, (job) => setExportProgress(job.percent));
      if (generation !== generationRef.current) return;
      const link = document.createElement("a");
      link.href = mediaUrl(ready.export.url); link.download = ready.export.filename; link.rel = "noreferrer";
      document.body.append(link); link.click(); link.remove();
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure.response ? apiErrorMessage(failure, "No se pudo exportar. Puedes reintentar.") : failure.message);
    } finally {
      if (generation === generationRef.current) { setExporting(false); exportRef.current = null; }
    }
  };
  return { rootRef, audioRef, phase, error, session, originalUrl, playing, setPlaying, playhead, setPlayhead,
    mode, pitch, ratio, progress, stage, exporting, exportProgress, upload, remove, changePitch, changeRatio,
    changeMode: (value) => change({ mode: value }), reset: () => change({ pitch: 0, ratio: 1 }),
    togglePlayback, seek, stop, loadFallback, exportAudio, ended: () => { playIntentRef.current = false; setPlaying(false); },
    paused: () => { if (!playPendingRef.current) { playIntentRef.current = false; setPlaying(false); } },
    retry: () => upload(fileRef.current, true) };
}
